import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Campaign } from '../../services/campaign.service';
import { ConfirmActionModalComponent } from '../../shared/components/confirm-action-modal';

/**
 * Modal dialog component that presents a confirmation UI for pausing a Campaign; it exposes actions to close the modal or confirm pausing the provided campaign.
 * @component
 * @selector app-pause-campaign-modal
 * @example
 *   <app-pause-campaign-modal [campaign]="selectedCampaign" (close)="onModalClose()" (confirmPause)="onConfirmPause($event)"></app-pause-campaign-modal>
 * @input Campaign | null campaign - The Campaign object to act upon; when null no campaign is targeted.
 * @output EventEmitter<void> close - Emitted when the modal is closed or cancelled without confirming the pause.
 * @output EventEmitter<Campaign> confirmPause - Emitted with the Campaign when the user confirms the pause action.
 *
 * Key methods:
 * - get isPaused(): boolean - A convenience getter that returns true when the current campaign's status equals 'paused'.
 * - onClose(): void - Emits the close event to signal the modal should be dismissed without changes.
 * - onConfirmPause(): void - Emits the confirmPause event with the current campaign when the user confirms pausing.
 *
 * Lifecycle hooks:
 * - This component does not implement Angular lifecycle hooks itself (no OnInit/OnDestroy used); it is a simple presentational/modal component.
 */
@Component({
  selector: 'app-pause-campaign-modal',
  standalone: true,
  imports: [CommonModule, ConfirmActionModalComponent],
  templateUrl: './pause-campaign-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./pause-campaign-modal.component.scss'],
})
export class PauseCampaignModalComponent {
  @Input() campaign: Campaign | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() confirmPause = new EventEmitter<Campaign>();

  get isPaused(): boolean {
    return this.campaign?.campaign_status === 'paused';
  }

  onClose(): void {
    this.close.emit();
  }

  onConfirmPause(): void {
    if (this.campaign) {
      this.confirmPause.emit(this.campaign);
    }
  }
}
